// Adapter entre las properties semánticas del GeoJSON y el display en UI.
// Consume los campos que getNetwork() ya expone en cada feature.properties.
// Sin dependencias de React, NestJS ni maplibre.

export interface SegmentSemanticProps {
  id?: string;
  road_class?: string | null;
  display_name?: string | null;
  osm_name?: string | null;
  street_name_resolved?: string | null;
  naming_status?: string | null;
  from_street_candidates?: string[];
  to_street_candidates?: string[];
}

export interface StreetSegmentLabel {
  primaryName:  string | null;
  fromStreet:   string | null;
  toStreet:     string | null;
  displayLabel: string;
  betweenLabel: string | null;
  confidence:   'high' | 'medium' | 'low' | 'unavailable';
  source:       string;
  warnings:     string[];
}

const ROAD_CLASS_NAMES: Record<string, string> = {
  motorway:       'Autopista',
  primary:        'Avenida principal',
  secondary:      'Calle secundaria',
  tertiary:       'Calle terciaria',
  residential:    'Calle residencial',
  service:        'Vía de servicio',
  unclassified:   'Calle sin clasificar',
  footway:        'Sendero peatonal',
  path:           'Camino',
  cycleway:       'Ciclovía',
  living_street:  'Calle compartida',
  motorway_link:  'Enlace de autopista',
  primary_link:   'Enlace de avenida',
  secondary_link: 'Enlace secundario',
  tertiary_link:  'Enlace terciario',
};

function roadClassName(roadClass: string | null | undefined): string {
  return roadClass ? (ROAD_CLASS_NAMES[roadClass] ?? roadClass) : 'Vía urbana';
}

export function resolveStreetSegmentLabel(props: SegmentSemanticProps): StreetSegmentLabel {
  const name      = props.street_name_resolved ?? props.osm_name ?? null;
  const status    = props.naming_status ?? 'unnamed';
  const fromList  = props.from_street_candidates ?? [];
  const toList    = props.to_street_candidates   ?? [];
  const warnings: string[] = [];

  // Sin nombre en BD
  if (!name) {
    return {
      primaryName:  null,
      fromStreet:   null,
      toStreet:     null,
      displayLabel: 'Tramo seleccionado',
      betweenLabel: null,
      confidence:   'unavailable',
      source:       'fallback_road_class',
      warnings:     ['no_name_in_db'],
    };
  }

  // Nombre disponible — evaluar si hay cruces
  if (status === 'named' && fromList.length === 1 && toList.length === 1) {
    return {
      primaryName:  name,
      fromStreet:   fromList[0],
      toStreet:     toList[0],
      displayLabel: name,
      betweenLabel: `entre ${fromList[0]} y ${toList[0]}`,
      confidence:   'high',
      source:       'osm_tags',
      warnings:     [],
    };
  }

  if (status === 'partial') {
    const from = fromList[0] ?? null;
    const to   = toList[0]   ?? null;
    let betweenLabel: string | null = null;
    if (from) betweenLabel = `desde ${from}`;
    if (to)   betweenLabel = `hasta ${to}`;
    warnings.push('partial_intersections');
    return {
      primaryName:  name,
      fromStreet:   from,
      toStreet:     to,
      displayLabel: name,
      betweenLabel,
      confidence:   'medium',
      source:       'osm_tags',
      warnings,
    };
  }

  // named pero ambiguous, missing_intersections u otro estado con nombre
  if (status === 'ambiguous')              warnings.push('ambiguous_intersections');
  if (status === 'missing_intersections')  warnings.push('no_topology_intersections');

  return {
    primaryName:  name,
    fromStreet:   null,
    toStreet:     null,
    displayLabel: name,
    betweenLabel: null,
    confidence:   status === 'named' ? 'medium' : 'low',
    source:       'osm_tags',
    warnings,
  };
}

export function streetSubLabel(
  label: StreetSegmentLabel,
  roadClass: string | null | undefined,
): string {
  if (label.betweenLabel && (label.confidence === 'high' || label.confidence === 'medium')) {
    return label.betweenLabel;
  }
  return `Tipo: ${roadClassName(roadClass).toLowerCase()}`;
}
